import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Edit, Upload, Users, Calendar, Copy, CheckSquare, RotateCcw, Clock } from "lucide-react";
import { ABOUT_DEFAULTS, ABOUT_TEXT_FIELDS, ABOUT_GAME_LABELS } from "@/lib/aboutContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function GameRulesCard() {
  const DAYS = [["Mon",0],["Tue",1],["Wed",2],["Thu",3],["Fri",4],["Sat",5],["Sun",6]];
  const GAMES = [["rebus","Rebus"],["sudoku","Sudoku"],["trivia","Trivia"],["caption","Caption"],["word","Word Building"],["pictionary","Pictionary"]];
  const [r, setR] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/game-rules").then((res) => setR(res.data)).catch(() => toast.error("Couldn't load game rules")); }, []);

  if (!r) return <div className="brutal-card p-5 text-sm text-neutral-500">Loading game rules…</div>;

  const toggleDay = (d) => {
    const set = new Set(r.active_days || []);
    set.has(d) ? set.delete(d) : set.add(d);
    setR({ ...r, active_days: [...set].sort((a,b)=>a-b) });
  };
  const toggleGated = (g) => {
    const set = new Set(r.gated_games || []);
    set.has(g) ? set.delete(g) : set.add(g);
    setR({ ...r, gated_games: [...set] });
  };
  const setPoint = (k, v) => setR({ ...r, points: { ...r.points, [k]: Number(v) } });

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/game-rules", r);
      setR(res.data);
      toast.success("Game rules saved — changes apply immediately");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="brutal-card p-5" data-testid="game-rules-card">
      <h3 className="font-black uppercase mb-1 flex items-center gap-2"><Calendar size={18}/> Game Rules (IST)</h3>
      <p className="text-sm text-neutral-600 mb-4">Control the daily play window, active days, submission limit, points and streaks. All times are <strong>Asia/Kolkata (IST)</strong>. Changes apply immediately.</p>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-black uppercase">Window opens (IST)</label>
          <input data-testid="gr-window-start" type="time" value={r.window_start} onChange={(e)=>setR({...r, window_start:e.target.value})} className="brutal-input"/>
        </div>
        <div>
          <label className="text-xs font-black uppercase">Window closes next-day (IST)</label>
          <input data-testid="gr-window-end" type="time" value={r.window_end} onChange={(e)=>setR({...r, window_end:e.target.value})} className="brutal-input"/>
        </div>
      </div>

      <div className="mt-4">
        <label className="text-xs font-black uppercase">Active days</label>
        <div className="flex flex-wrap gap-2 mt-1">
          {DAYS.map(([label,d]) => (
            <button key={d} data-testid={`gr-day-${d}`} onClick={()=>toggleDay(d)} className={`px-3 py-1 border-2 border-black text-xs font-black uppercase ${ (r.active_days||[]).includes(d) ? "bg-black text-white" : "bg-white hover:bg-[#FFD600]"}`}>{label}</button>
          ))}
        </div>
        <p className="text-[11px] text-neutral-500 mt-1">Days not selected show employees a “take a break” message and block gated games.</p>
      </div>

      <div className="mt-4" data-testid="gr-schedule-preview">
        <label className="text-xs font-black uppercase">Next 7 days preview</label>
        <p className="text-[11px] text-neutral-500 mb-2">Live preview of your current (unsaved) settings, in IST.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {(() => {
            const names = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
            const istNow = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
            const out = [];
            for (let i = 0; i < 7; i++) {
              const d = new Date(istNow);
              d.setDate(d.getDate() + i);
              const py = (d.getDay() + 6) % 7; // JS Sun=0 -> Python Mon=0
              const open = (r.active_days || []).includes(py);
              const dateStr = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
              out.push(
                <div key={i} data-testid={`gr-preview-day-${i}`} className="flex items-center justify-between border-2 border-black px-3 py-1.5 text-xs" style={{ background: open ? "#EAFBE7" : "#FBE8E8" }}>
                  <span className="font-black uppercase">{i === 0 ? "Today" : names[py]} <span className="text-neutral-500 font-normal">{dateStr}</span></span>
                  <span className="font-black uppercase" style={{ color: open ? "#1a7f37" : "#b91c1c" }}>
                    {open ? `Open ${r.window_start}→${r.window_end}` : "Closed"}
                  </span>
                </div>
              );
            }
            return out;
          })()}
        </div>
      </div>

      <div className="mt-4">
        <label className="text-xs font-black uppercase">Time-gated games</label>
        <div className="flex flex-wrap gap-2 mt-1">
          {GAMES.map(([key,label]) => (
            <button key={key} data-testid={`gr-gated-${key}`} onClick={()=>toggleGated(key)} className={`px-3 py-1 border-2 border-black text-xs font-black uppercase ${ (r.gated_games||[]).includes(key) ? "bg-[#39FF14]" : "bg-white hover:bg-neutral-100"}`}>{label}</button>
          ))}
        </div>
        <p className="text-[11px] text-neutral-500 mt-1">Only selected games obey the window/active-days. Others stay always-on.</p>
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-black uppercase">Max submissions per game / day</label>
          <input data-testid="gr-max-subs" type="number" min={0} value={r.max_submissions_per_day} onChange={(e)=>setR({...r, max_submissions_per_day:Number(e.target.value)})} className="brutal-input"/>
        </div>
        <div>
          <label className="text-xs font-black uppercase flex items-center gap-1"><Clock size={13}/> Daily play time (minutes)</label>
          <input data-testid="gr-daily-play-minutes" type="number" min={0} value={r.daily_play_minutes ?? 30} onChange={(e)=>setR({...r, daily_play_minutes:Number(e.target.value)})} className="brutal-input"/>
          <p className="text-[11px] text-neutral-500 mt-1">Each employee can play games for this many minutes per day (resets midnight IST). Set <strong>0</strong> for unlimited.</p>
        </div>
      </div>

      <div className="mt-4">
        <label className="text-xs font-black uppercase">Points</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
          {[["submit","Submit"],["correct","Correct"],["close","Close"],["top3_fastest","Top-3 fastest"]].map(([k,label]) => (
            <div key={k}>
              <div className="text-[11px] uppercase font-mono text-neutral-500">{label}</div>
              <input data-testid={`gr-points-${k}`} type="number" min={0} value={r.points?.[k] ?? 0} onChange={(e)=>setPoint(k, e.target.value)} className="brutal-input"/>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 grid md:grid-cols-3 gap-3">
        <label className="border-2 border-black p-3 flex items-center justify-between">
          <span className="font-black uppercase text-sm">Streaks enabled</span>
          <Switch data-testid="gr-streak-enabled" checked={!!r.streak?.enabled} onCheckedChange={(v)=>setR({...r, streak:{...r.streak, enabled:v}})}/>
        </label>
        <label className="border-2 border-black p-3 flex items-center justify-between">
          <span className="font-black uppercase text-sm">Count weekends</span>
          <Switch data-testid="gr-streak-weekends" checked={!!r.streak?.count_weekends} onCheckedChange={(v)=>setR({...r, streak:{...r.streak, count_weekends:v}})}/>
        </label>
        <div>
          <div className="text-[11px] uppercase font-mono text-neutral-500">Streak +per day</div>
          <input data-testid="gr-streak-increment" type="number" min={1} value={r.streak?.increment ?? 1} onChange={(e)=>setR({...r, streak:{...r.streak, increment:Number(e.target.value)}})} className="brutal-input"/>
        </div>
      </div>

      <button data-testid="gr-save-btn" onClick={save} disabled={saving} className="brutal-btn mt-5">{saving ? "Saving…" : "Save Game Rules"}</button>
    </div>
  );
}


function SettingsPanel() {
  const [s, setS] = useState({});
  const [pwForm, setPwForm] = useState({ current_password:"", new_password:"" });
  const [reportsPw, setReportsPw] = useState("");
  const [games, setGames] = useState({ rebus: true, sudoku: true, word_building: true, trivia: true, caption: true, pictionary: true });
  useEffect(()=>{ api.get("/admin/settings").then(r=>{ setS(r.data); if(r.data.games_enabled) setGames(r.data.games_enabled); }); }, []);

  const save = async () => { await api.put("/admin/settings", s); toast.success("Settings saved"); };
  const saveGames = async () => {
    await api.put("/admin/settings", { ...s, games_enabled: games });
    toast.success("Game availability updated");
  };
  const changePw = async () => {
    try { await api.post("/admin/change-password", pwForm); toast.success("Password changed"); setPwForm({current_password:"", new_password:""}); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const changeReportsPw = async () => {
    if (reportsPw.trim().length < 4) return toast.error("Password must be at least 4 characters");
    try { await api.post("/admin/change-reports-password", { new_password: reportsPw.trim() }); toast.success("Reports password updated"); setReportsPw(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const apiUrl = `${BACKEND_URL}/api/api-sync/users`;
  const pullUrl = `${apiUrl}?api_token=${s.ats_api_token || "<token>"}`;
  const copyApi = () => { navigator.clipboard.writeText(apiUrl); toast.success("API URL copied"); };
  const copyPull = () => { navigator.clipboard.writeText(pullUrl); toast.success("Pull URL copied"); };
  const copyToken = () => { navigator.clipboard.writeText(s.ats_api_token || ""); toast.success("Token copied"); };

  const [resetting, setResetting] = useState(false);
  const resetLeaderboard = async () => {
    if (!confirm("Wipe ALL submissions, scores, streaks, audit logs and notifications? Users / tokens / themes / avatars are kept. This cannot be undone.")) return;
    setResetting(true);
    try {
      const r = await api.post("/admin/leaderboard/reset");
      const totals = Object.entries(r.data.deleted || {}).map(([k,v])=>`${k}: ${v}`).join("  ·  ");
      toast.success("Leaderboards reset", { description: totals });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setResetting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="brutal-card p-5">
        <h3 className="font-black uppercase mb-3 flex items-center gap-2"><CheckSquare size={18}/> Games (Enable / Disable)</h3>
        <p className="text-sm text-neutral-600 mb-3">Toggle which games are visible on the employee hub. Disabling a game hides its card.</p>
        <div className="grid md:grid-cols-3 gap-3">
          {[
            { key: "rebus", label: "Rebus Daily" },
            { key: "sudoku", label: "Sudoku Daily" },
            { key: "word_building", label: "Word Building" },
            { key: "trivia", label: "Trivia of the Day" },
            { key: "caption", label: "Photo Caption" },
            { key: "pictionary", label: "Office Pictionary" },
          ].map((g) => (
            <label key={g.key} className="border-2 border-black p-3 flex items-center justify-between cursor-pointer" style={{background: games[g.key] ? "#FFFCE5" : "white"}}>
              <span className="font-black uppercase">{g.label}</span>
              <input data-testid={`toggle-${g.key}`} type="checkbox" checked={!!games[g.key]} onChange={(e)=>setGames({...games, [g.key]: e.target.checked})} className="w-5 h-5"/>
            </label>
          ))}
        </div>
        <button data-testid="save-games" onClick={saveGames} className="brutal-btn mt-4">Save Game Settings</button>
      </div>

      <div className="brutal-card p-5 border-[#FF3B30]" data-testid="leaderboard-reset-card">
        <h3 className="font-black uppercase mb-2 flex items-center gap-2" style={{color: "#FF3B30"}}><RotateCcw size={18}/> Leaderboard Reset</h3>
        <p className="text-sm text-neutral-700 mb-3">
          Wipe <strong>all submissions, scores, streaks, audit logs and notifications</strong> so the leaderboards start fresh.
          Users, tokens, themes and avatars are kept intact. Use this to launch a new season.
          <br/><strong className="text-[#FF3B30]">This action cannot be undone.</strong>
        </p>
        <button
          data-testid="leaderboard-reset-btn"
          onClick={resetLeaderboard}
          disabled={resetting}
          className="brutal-btn flex items-center gap-2 bg-[#FF3B30] text-white border-black hover:opacity-90"
        >
          <RotateCcw size={14}/> {resetting ? "Wiping…" : "Reset Leaderboards"}
        </button>
      </div>

      <GameRulesCard/>

      <div className="brutal-card p-5">
        <h3 className="font-black uppercase mb-3">ATS Integration Methods</h3>
        <p className="text-sm text-neutral-700 mb-4">Connect your ATS in one of four ways. Place the user-specific play link in your ATS, then employees enter without login.</p>

        <div className="space-y-4">
          <div className="border-l-4 border-[#FF3B30] pl-4">
            <h4 className="font-black uppercase">A. CSV Import</h4>
            <p className="text-sm text-neutral-600">Upload <code>employee_id,name,email</code> in Users tab. Quickest.</p>
          </div>
          <div className="border-l-4 border-[#FF3B30] pl-4">
            <h4 className="font-black uppercase">B. REST API Webhook</h4>
            <p className="text-sm text-neutral-600">Your ATS POSTs users to this endpoint:</p>
            <div className="flex items-center gap-2 mt-1 bg-neutral-100 p-2 border-2 border-neutral-300 font-mono text-xs">
              <span className="flex-1 truncate">{apiUrl}</span>
              <button onClick={copyApi} className="p-1 border-2 border-black"><Copy size={12}/></button>
            </div>
            <div className="mt-2">
              <label className="text-xs font-black uppercase">API Token</label>
              <div className="flex items-center gap-2 bg-neutral-100 p-2 border-2 border-neutral-300 font-mono text-xs">
                <span className="flex-1 truncate">{s.ats_api_token}</span>
                <button onClick={copyToken} className="p-1 border-2 border-black"><Copy size={12}/></button>
              </div>
            </div>
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer font-bold">Show JSON payload format</summary>
              <pre className="bg-black text-[#39FF14] p-3 mt-2 overflow-x-auto">{`POST ${apiUrl}
Content-Type: application/json

{
  "api_token": "${s.ats_api_token || "<token>"}",
  "users": [
    { "employee_id": "EMP001", "name": "Alice", "email": "a@co.com" }
  ]
}`}</pre>
            </details>
          </div>
          <div className="border-l-4 border-[#FF3B30] pl-4">
            <h4 className="font-black uppercase">C. SSO Token Links (No Login)</h4>
          <p className="text-sm text-neutral-600">{`Each user has a token. Embed in your ATS:`}</p>
            <div className="bg-neutral-100 p-2 border-2 border-neutral-300 font-mono text-xs mt-1">
              {window.location.origin}/play?token=&lt;EMPLOYEE_TOKEN&gt;
            </div>
            <p className="text-xs mt-2 text-neutral-500">Use the Copy icon next to each user in the Users tab to grab their personal link.</p>
          </div>
          <div className="border-l-4 border-[#16a34a] pl-4" data-testid="ats-pull-users">
            <h4 className="font-black uppercase">D. Pull Users API (Export to your DB)</h4>
            <p className="text-sm text-neutral-600">Fetch the full FunZone user list to sync into your ATS / local DB. Returns <strong>Employee ID, Name, Email, Department, Token, Created At</strong>. Poll on a schedule to keep both systems in sync.</p>
            <div className="flex items-center gap-2 mt-1 bg-neutral-100 p-2 border-2 border-neutral-300 font-mono text-xs">
              <span className="flex-1 truncate">GET {pullUrl}</span>
              <button data-testid="copy-pull-url" onClick={copyPull} className="p-1 border-2 border-black"><Copy size={12}/></button>
            </div>
            <p className="text-[11px] text-neutral-500 mt-1">Auth options: <code>?api_token=</code> query param, <code>X-API-Token</code> header, or <code>Authorization: Bearer &lt;token&gt;</code>.</p>
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer font-bold">Show JSON response format</summary>
              <pre className="bg-black text-[#39FF14] p-3 mt-2 overflow-x-auto">{`GET ${apiUrl}
X-API-Token: ${s.ats_api_token || "<token>"}

200 OK
{
  "count": 128,
  "users": [
    {
      "employee_id": "EMP001",
      "name": "Alice Kumar",
      "email": "alice@tekwissen.com",
      "department": "Engineering",
      "token": "aBc123XyZ...",
      "created_at": "2026-06-01T09:12:00+00:00"
    }
  ]
}`}</pre>
            </details>
          </div>
        </div>
      </div>

      <AboutEditorCard />

      <div className="brutal-card p-5">
        <h3 className="font-black uppercase mb-3">Change Admin Password</h3>
        <div className="space-y-2 max-w-md">
          <input data-testid="current-pw-input" type="password" placeholder="Current password" className="brutal-input" value={pwForm.current_password} onChange={(e)=>setPwForm({...pwForm, current_password:e.target.value})}/>
          <input data-testid="new-pw-input" type="password" placeholder="New password" className="brutal-input" value={pwForm.new_password} onChange={(e)=>setPwForm({...pwForm, new_password:e.target.value})}/>
          <button data-testid="change-pw-btn" onClick={changePw} className="brutal-btn">Change Password</button>
        </div>
      </div>

      <div className="brutal-card p-5">
        <h3 className="font-black uppercase mb-3">Change Reports Portal Password</h3>
        <p className="text-sm text-neutral-600 mb-3">Password for the read-only Reports Portal login (<code>/reports</code>). No current password needed.</p>
        <div className="space-y-2 max-w-md">
          <input data-testid="reports-new-pw-input" type="password" placeholder="New reports password" className="brutal-input" value={reportsPw} onChange={(e)=>setReportsPw(e.target.value)}/>
          <button data-testid="change-reports-pw-btn" onClick={changeReportsPw} className="brutal-btn">Set Reports Password</button>
        </div>
      </div>
    </div>
  );
}


function AboutEditorCard() {
  const [about, setAbout] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      const a = r.data.about || {};
      setAbout({ ...ABOUT_DEFAULTS, ...a, games: { ...ABOUT_DEFAULTS.games, ...(a.games || {}) } });
    }).catch(() => setAbout({ ...ABOUT_DEFAULTS, games: { ...ABOUT_DEFAULTS.games } }));
  }, []);

  const setField = (k, v) => setAbout((p) => ({ ...p, [k]: v }));
  const setGame = (k, v) => setAbout((p) => ({ ...p, games: { ...p.games, [k]: v } }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/about", about); toast.success("About page updated"); }
    catch { toast.error("Failed to save About page"); }
    finally { setSaving(false); }
  };
  const resetDefaults = () => setAbout({ ...ABOUT_DEFAULTS, games: { ...ABOUT_DEFAULTS.games } });

  if (!about) return null;

  return (
    <div className="brutal-card p-5" data-testid="about-editor">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h3 className="font-black uppercase">Edit About Page</h3>
        <div className="flex gap-2">
          <button data-testid="about-reset-btn" onClick={resetDefaults} className="brutal-btn-secondary text-sm">Reset to Defaults</button>
          <button data-testid="about-save-btn" onClick={save} disabled={saving} className="brutal-btn text-sm disabled:opacity-50">{saving ? "Saving…" : "Save About Page"}</button>
        </div>
      </div>
      <p className="text-sm text-neutral-600 mb-4">Edit the guidelines and game descriptions shown on the public <code>/about</code> page. Timings, play-time, points and shop values update automatically from Game Rules &amp; Shop settings.</p>

      <div className="space-y-4">
        {ABOUT_TEXT_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-[11px] font-black uppercase block mb-1">{f.label}{f.hint && <span className="ml-2 font-mono text-[10px] text-neutral-400 normal-case">{f.hint}</span>}</label>
            <textarea data-testid={`about-field-${f.key}`} value={about[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} rows={f.key === "cheating" ? 2 : 3} className="brutal-input w-full text-sm" />
          </div>
        ))}

        <div>
          <label className="text-[11px] font-black uppercase block mb-2">Game Descriptions</label>
          <div className="space-y-3">
            {Object.keys(ABOUT_GAME_LABELS).map((gk) => (
              <div key={gk}>
                <div className="text-xs font-bold mb-1">{ABOUT_GAME_LABELS[gk]}</div>
                <textarea data-testid={`about-game-field-${gk}`} value={about.games[gk] || ""} onChange={(e) => setGame(gk, e.target.value)} rows={2} className="brutal-input w-full text-sm" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPanel;
